import React from 'react';
import { motion } from 'motion/react';
import { Bolt, ArrowUpRight } from 'lucide-react';

const StepDot = ({ isActive, delay }: { isActive: boolean; delay: number }) => (
  <motion.div
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ delay, duration: 0.3 }}
    className={`h-2 rounded-full transition-all ${
      isActive ? 'w-8 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500' : 'w-2 bg-white'
    }`}
  />
);

const CreateOrganizationStepTwoCard: React.FC = () => {
  return (
    <div className="relative flex h-full w-full flex-col justify-between rounded-r-4xl bg-blue-200 p-6 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bolt className="h-5 w-5 text-blue-600" />
          <span className="font-semibold">Enexan</span>
        </div>
        <a
          href="https://www.enexan.ch"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-base font-semibold text-gray-700 shadow-lg transition-colors hover:text-blue-600"
        >
          enexan.ch
          <ArrowUpRight className="h-5 w-5" />
        </a>
      </div>

      {/* Content - 80% height */}
      <div className="relative flex items-center justify-center overflow-hidden" style={{ height: '80vh' }}>
        {/* Background Text - Parallax Effect */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <h1 className="text-[12rem] leading-none font-black tracking-tighter text-white/20 select-none">POWER</h1>
        </motion.div>

        {/* Floating Phoenix - Slightly in front */}
        <motion.div
          initial={{ opacity: 0, y: 50, rotate: -5 }}
          animate={{
            opacity: 1,
            y: 0,
            rotate: 0,
            scale: [1, 1.02, 1],
          }}
          transition={{
            duration: 1.2,
            ease: 'easeOut',
            scale: {
              duration: 3,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'easeInOut',
            },
          }}
          className="relative z-10"
        >
          <img
            src="/assets/lightning.png"
            alt="Phoenix"
            className="h-[500px] w-[400px] object-contain drop-shadow-2xl"
          />
        </motion.div>

        {/* Animated Sparkles */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 2, repeat: Infinity, delay: 1 }}
          className="absolute top-20 right-16 h-2 w-2 rounded-full bg-blue-300 shadow-lg"
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, delay: 2 }}
          className="absolute bottom-24 left-20 h-1.5 w-1.5 rounded-full bg-red-300 shadow-lg"
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, delay: 0.5 }}
          className="absolute top-32 left-24 h-1 w-1 rounded-full bg-yellow-300 shadow-lg"
        />

        {/* Floating Orbs */}
        <motion.div
          animate={{
            y: [-10, 10, -10],
            x: [-5, 5, -5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute top-16 left-16 h-8 w-8 rounded-full bg-gradient-to-br from-blue-200/40 to-red-300/40 blur-sm"
        />

        <motion.div
          animate={{
            y: [10, -10, 10],
            x: [5, -5, 5],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
          className="absolute right-20 bottom-16 h-6 w-6 rounded-full bg-gradient-to-br from-yellow-200/40 to-blue-300/40 blur-sm"
        />
      </div>

      {/* Step Dots */}
      <div className="flex justify-center gap-2 pt-4">
        <StepDot isActive={false} delay={0.1} />
        <StepDot isActive={true} delay={0.2} />
        <StepDot isActive={false} delay={0.3} />
      </div>
    </div>
  );
};

export default CreateOrganizationStepTwoCard;
